import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchApi } from "@/lib/api";
import { CLERK_AVAILABLE, getDemoAuthHeaders, getClerkAuthHeaders } from "@/lib/auth";
import { useAuthConditional } from "@/lib/use-auth-conditional";

export interface Commitment {
  id: string;
  projectId: string;
  costCodeId: string;
  costCode: string;
  costCodeName: string;
  vendorName: string;
  type: "po" | "subcontract";
  amount: number;
  description: string | null;
  date: string;
  status: "draft" | "issued" | "received" | "paid";
  createdAt: string;
}

export interface CreateCommitmentData {
  costCodeId: string;
  vendorName: string;
  type: "po" | "subcontract";
  amount: number;
  description?: string;
  date: string;
  status?: "draft" | "issued" | "received" | "paid";
}

export interface UpdateCommitmentData {
  vendorName?: string;
  amount?: number;
  description?: string;
  status?: "draft" | "issued" | "received" | "paid";
}

interface CommitmentsResponse {
  commitments: Commitment[];
  summary: {
    totalCommitments: number;
    totalAmount: number;
    byType: {
      po: number;
      subcontract: number;
    };
    byStatus: {
      draft: number;
      issued: number;
      received: number;
      paid: number;
    };
  };
}

interface CommitmentResponse {
  commitment: Commitment;
}

export interface FinancialSummary {
  budget: number;
  committed: number;
  actual: number;
  remaining: number;
  variance: number;
  percentCommitted: number;
  percentSpent: number;
}

interface FinancialSummaryResponse {
  summary: FinancialSummary;
}

// Get all commitments for a project
export function useCommitments(
  projectId: string,
  options?: { type?: "po" | "subcontract"; status?: string }
) {
  const auth = useAuthConditional();

  return useQuery({
    queryKey: ["projects", projectId, "commitments", options],
    queryFn: async () => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      const params = new URLSearchParams();
      if (options?.type) params.set("type", options.type);
      if (options?.status) params.set("status", options.status);
      const queryString = params.toString();
      const url = `/api/projects/${projectId}/commitments${queryString ? `?${queryString}` : ""}`;

      return fetchApi<CommitmentsResponse>(url, { headers });
    },
    enabled: auth.isSignedIn && !!projectId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Get a single commitment
export function useCommitment(projectId: string, commitmentId: string) {
  const auth = useAuthConditional();

  return useQuery({
    queryKey: ["projects", projectId, "commitments", commitmentId],
    queryFn: async () => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<CommitmentResponse>(
        `/api/projects/${projectId}/commitments/${commitmentId}`,
        { headers }
      );
    },
    enabled: auth.isSignedIn && !!projectId && !!commitmentId,
    staleTime: 1000 * 60 * 5,
  });
}

// Create a new commitment
export function useCreateCommitment(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateCommitmentData) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<CommitmentResponse>(`/api/projects/${projectId}/commitments`, {
        method: "POST",
        headers,
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "commitments"] });
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "financial-summary"] });
    },
  });
}

// Update a commitment
export function useUpdateCommitment(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateCommitmentData }) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<CommitmentResponse>(`/api/projects/${projectId}/commitments/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(data),
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "commitments"] });
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "commitments", variables.id],
      });
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "financial-summary"] });
    },
  });
}

// Delete a commitment
export function useDeleteCommitment(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<{ success: boolean }>(`/api/projects/${projectId}/commitments/${id}`, {
        method: "DELETE",
        headers,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "commitments"] });
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "financial-summary"] });
    },
  });
}

// Get financial summary (Budget vs Committed vs Actual)
export function useFinancialSummary(projectId: string) {
  const auth = useAuthConditional();

  return useQuery({
    queryKey: ["projects", projectId, "financial-summary"],
    queryFn: async () => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<FinancialSummaryResponse>(
        `/api/projects/${projectId}/financial-summary`,
        { headers }
      );
    },
    enabled: auth.isSignedIn && !!projectId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}
